import api from './api';
import { API_ENDPOINTS } from './config';

/**
 * Project Service
 * Handles project creation, crew/equipment matching, and project brief generation
 */

/**
 * Create a new project
 * @param {FormData} formData - Project data including files
 * @returns {Promise<Object>} { success, data: { project_id, ... }, error }
 */
export const createProject = async (data) => {
  try {
    const response = await api.post(API_ENDPOINTS.PROJECTS.CREATE, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (error) {
    console.error('Create Project Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to create project',
    };
  }
};

/**
 * Match crew members for a project
 * @param {Object} data - { project_id }
 * @returns {Promise<Object>} { success, data: { matched_crew: [...] }, error }
 */
export const matchCrew = async (data) => {
  try {
    const response = await api.post(API_ENDPOINTS.PROJECTS.MATCH_CREW, data,
      {
        headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        "Content-Type": "application/json",
        "Accept": "application/json",
       },
      });
    return response;
  } catch (error) {
    console.error('Match Crew Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to match crew members',
    };
  }
};

/**
 * Assign matched crew members to a project
 * @param {Object} data - { project_id, crew_ids: [...] }
 * @returns {Promise<Object>} { success, data, error }
 */
export const assignCrew = async (data) => {
  try {
    const response = await api.post(API_ENDPOINTS.PROJECTS.ASSIGN_CREW, data);
    return response;
  } catch (error) {
    console.error('Assign Crew Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to assign crew members',
    };
  }
};

/**
 * Match equipment for a project
 * @param {Object} data - { project_id }
 * @returns {Promise<Object>} { success, data: { matched_equipment: [...] }, error }
 */
export const matchEquipment = async (data) => {
  try {
    const response = await api.post(API_ENDPOINTS.PROJECTS.MATCH_EQUIPMENT, data, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
         },
      });
    return response;
  } catch (error) {
    console.error('Match Equipment Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to match equipment',
    };
  }
};

/**
 * Assign matched equipment to a project
 * @param {Object} data - { project_id, equipment_ids: [...] }
 * @returns {Promise<Object>} { success, data, error }
 */
export const assignEquipment = async (data) => {
  try {
    const response = await api.post(API_ENDPOINTS.PROJECTS.ASSIGN_EQUIPMENT, data);
    return response;
  } catch (error) {
    console.error('Assign Equipment Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to assign equipment',
    };
  }
};

/**
 * Create final project brief
 * @param {FormData} formData - Project brief data including files
 * @returns {Promise<Object>} { success, data, error }
 */
export const createProjectBrief = async (formData) => {
  try {
    const response = await api.post(API_ENDPOINTS.PROJECTS.CREATE_BRIEF, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    console.error('Create Project Brief Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to create project brief',
    };
  }
};

/**
 * Get project details by ID
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} { success, data: { project details }, error }
 */
export const getProject = async (projectId) => {
  try {
    const response = await api.get(`${API_ENDPOINTS.PROJECTS.GET}/${projectId}`);
    return response;
  } catch (error) {
    console.error('Get Project Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to fetch project details',
    };
  }
};

/**
 * Fetch all projects
 * @returns {Promise<Object>}
 */
export const getAllProject = async (limit = 50, page = 1) => {
  try {
    let params = {};

    // Case 1 → If limit is a string, treat it as a status
    if (typeof limit === "string") {
      params.status = limit;   // "completed" | "cancelled" | "draft"
    }
    else {
      // Case 2 → Normal pagination mode
      params.limit = limit;
      params.page = page;
    }

    const response = await api.get(API_ENDPOINTS.PROJECTS.GET_ALL_PROJECT, {
      params,
    });

    return response;

  } catch (error) {
    console.error("Get All Project Error:", error);
    return {
      success: false,
      data: null,
      error: "Failed to fetch projects",
    };
  }
};

export const previewProjectCsv = async (formData) => {
  try {
    // This will always be FormData because we are uploading a file
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    const response = await api.post(
      API_ENDPOINTS.PROJECTS.BULK_PREVIEW || '/api/admin/bulk-upload-project',
      formData,
      config
    );
    return response;
  } catch (error) {
    console.error('Preview Crew CSV Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to parse crew CSV file',
    };
  }
};

/**
 * Step 2: Save the mapped crew data to the database
 * @param {Object} data - Format: { crew_list: [...] }
 */
export const bulkSaveProjects = async (data) => {
  try {
    // This is standard JSON data
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const response = await api.post(
      API_ENDPOINTS.PROJECTS.BULK_SAVE || '/api/admin/bulk-upload-save-project',
      data,
      config
    );
    return response;
  } catch (error) {
    console.error('Bulk Save Crew Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to save crew members',
    };
  }
};

// Add this to your projectService.js
export const updateProject = async (projectId, data) => {
  try {
    const response = await api.put(`/api/admin/update-project/${projectId}`, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (error) {
    console.error('Update Project Error:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data?.message || 'Failed to update project',
    };
  }
};
